import React from 'react';
import ReactApexChart from 'react-apexcharts';

interface ProgressBarProps {
  total: number;
  doctorCoverage: number;
  vfCoverage: number;
  doctorLabel: string;
  vfLabel: string;
  showTwoLines: boolean;
  title?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  total,
  doctorCoverage,
  vfCoverage,
  doctorLabel,
  vfLabel,
  showTwoLines,
  title = "Team Coverage Compliance",
}) => {
  const doctorPercent = (doctorCoverage / total) * 100;
  const vfPercent = (vfCoverage / total) * 100;

  // Logic for series array based on `showTwoLines`
  const series = showTwoLines
    ? [vfPercent, doctorPercent - vfPercent] // Two bars when showTwoLines is true
    : [doctorPercent]; // Only one bar when showTwoLines is false

  // Define colors based on `showTwoLines`
  const colors = showTwoLines
    ? ['#3375B4', '#61A850'] // Two different colors when two bars are visible
    : ['#3375B4']; // Only one color when one bar is visible

  // Define labels based on `showTwoLines`
  const labels = showTwoLines ? [vfLabel, doctorLabel] : [doctorLabel];

  const options = {
    chart: {
      type: 'radialBar' as const,
      offsetY: -10,
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {
      radialBar: {
        startAngle: -100,
        endAngle: 100,
        track: {
          background: '#e7e7e7',
          strokeWidth: '90%',
          margin: 3,
        },
        hollow: {
          size: '50%',
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            show: false,
          },
        },
      },
    },
    stroke: {
      lineCap: 'butt' as const,
    },
    colors, // Colors array which will be based on `showTwoLines`
    labels,
    tooltip: {
      enabled: showTwoLines, // Tooltip only enabled when both bars are shown
    },
  };

  return (
    <div
      style={{
        background: '#fff',
        padding: '16px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        width: '100%',
        margin: '0 auto',
      }}
    >
      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px' }}>
        {title}
      </h3>

      <ReactApexChart
        options={options}
        series={series}
        type="radialBar"
        height={280}
      />

      <div style={{ marginTop: '10px', fontSize: '13px' }}>
        <div
          style={{
            color: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              background: '#3b82f6',
              borderRadius: '2px',
            }}
          />
          {doctorLabel}: {doctorCoverage} / {total}
        </div>

        {showTwoLines && (
          <div
            style={{
              color: '#22c55e',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'center',
              marginTop: '4px',
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                background: '#22c55e',
                borderRadius: '2px',
              }}
            />
            {vfLabel}: {vfCoverage} / {total}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
